import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { employeeActions } from '@nxsys/store/slices/employee/reducer/employeeSlice';
import { GetYTDHistoryEmployeeListParams } from '@nxsys/store/slices/employee/types';
import { Icons } from '@nxsys/types/icons';
import { ILeaverHistory } from '@nxsys/types/models/employment';
import { showCurrencyByCountry } from '@nxsys/utils';
import { formatMoney } from '@nxsys/utils/number-helper';
import { Typography } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { toNumber } from 'lodash';
import moment from 'moment';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

type Props = {
  employeeId: string;
};

const EmploymentHistoryTab: React.FC<Props> = ({ employeeId }) => {
  const { messages } = useTypedIntl();
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);
  const dispatch = useDispatch<AppDispatch>();

  const { leaverHistoryList } = useSelector(
    (state: AppState) => state.employeeStore,
  );
  const { loading, list, pagination, queryParams } = leaverHistoryList;

  useEffect(() => {
    handleFetchData({ page: 0, size: 20 });
  }, []);

  const handleFetchData = (params: GetYTDHistoryEmployeeListParams) => {
    const newParams = { ...params, id: employeeId };
    dispatch(employeeActions.getLeaverHistoryEmployeeList(newParams));
  };

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const columns: ColumnsType<ILeaverHistory> = [
    {
      key: 'action',
      title: messages['common.action'],
      fixed: 'left',
      align: 'left',
      width: 100,
      render: (record) => (
        <ul className="table-actions flex items-center justify-start">
          <li onClick={() => window.open(record.url, '_blank')}>
            <Icons.IconDownload />
          </li>
        </ul>
      ),
    },
    {
      key: 'employmentId',
      title: messages['common.employmentId'],
      width: 180,
      render: (record) => record?.employmentId || '-',
    },
    {
      key: 'taxYear',
      title: messages['common.taxYear'],
      dataIndex: 'taxYear',
      width: 180,
    },
    {
      key: 'dateOfJoining',
      title: messages['common.dateOfJoining'],
      dataIndex: 'dateOfJoining',
      width: 180,
      render: (value) => (value ? moment(value).format('DD/MM/YYYY') : '-'),
    },
    {
      key: 'dateLeft',
      title: 'Date Left',
      dataIndex: 'dateLeft',
      width: 180,
      render: (value) => (value ? moment(value).format('DD/MM/YYYY') : '-'),
    },
    {
      key: 'taxCode',
      dataIndex: 'taxCode',
      title: messages['common.taxCode'],
      width: 180,
    },
    {
      key: 'taxBasis',
      dataIndex: 'taxBasics',
      title: messages['common.taxBasis'],
      width: 180,
    },
    {
      key: 'totalPayToDate',
      dataIndex: 'totalPayToDate',
      title: messages['common.totalPayToDate'],
      width: 180,
      render: (value) =>
        value
          ? formatMoney(toNumber(value), showCurrencyByCountry(payrollCountry))
          : '-',
    },
    {
      key: 'totalTaxToDate',
      dataIndex: 'totalTaxToDate',
      title: messages['common.totalTaxToDate'],
      width: 180,
      render: (value) =>
        value
          ? formatMoney(toNumber(value), showCurrencyByCountry(payrollCountry))
          : '-',
    },
  ];

  return (
    <div>
      <Typography.Title
        level={5}
        className="!mb-6 !text-[20px] !leading-[1.6] !tracking-[-0.02em] !text-[#1A1D1F]"
      >
        {messages['common.employmentHistory']}
      </Typography.Title>
      <AppsContent>
        <AppTableContainer
          rowKey="id"
          data={list}
          loading={loading}
          page={pagination.current}
          total={pagination.totalDocs}
          pageSize={pagination.pageSize}
          scroll={{ x: 1500, y: 700 }}
          columns={columns}
          onChangePagination={onChangePagination}
        />
      </AppsContent>
    </div>
  );
};

export default EmploymentHistoryTab;
